<template>
  <!--
    Program is rendered as content inside LiveStream's `main` slot so
    both pages share the EXACT same outer chrome — Navbar, page
    wrapper, side panel (icon rail + promoted message + every drill-
    down tab) and dark/light theming. Clicking any sidepanel icon
    opens the corresponding panel in-place; no route change.
  -->
  <LiveStream>
    <!-- Mobile only: greeting + My Programs pills, lifted out of the hero
         banner and rendered above the promoted message (see LiveStream's
         `mobile-top` slot). On desktop these stay overlaid on the banner. -->
    <template #mobile-top>
      <div class="prog-mobile-pills">
        <div class="prog-mobile-pills__greeting">
          <Avatar
            :image="(getUser && getUser.Picture) || null"
            :circle="true"
            :name="welcomeName"
            :alt="welcomeName"
            :aria-label="welcomeName"
            class="prog-hero__avatar"
          />
          <span class="prog-hero__hi">{{ welcomePrefix }}</span>
          <span class="prog-hero__name">{{ welcomeName }}</span>
          <img
            class="prog-hero__wave"
            src="https://fonts.gstatic.com/s/e/notoemoji/latest/1f44b/512.gif"
            alt=""
            aria-hidden="true"
            loading="lazy"
          />
        </div>
        <button
          v-if="isBookingsEnabled"
          type="button"
          class="prog-mobile-pills__cta"
          @click="goToMyPrograms"
        >
          <i class="fas fa-star" aria-hidden="true"></i>
          <span>{{ $t('Views.Program.myPrograms') }}</span>
        </button>
      </div>
    </template>

    <template #main>
      <CurrentEventDataProvider
        v-slot="{ hasFullEventDetails, data, features }"
      >
        <div class="program-page program-page--embedded">
          <div v-if="hasFullEventDetails" id="main-content" role="main">
            <BookSessionsDataProvider
              v-slot="{
                bookSessions,
                addOrDeleteBooksSesions,
                isBookSessionLoading
              }"
            >
              <AgendasDataProvider
                v-slot="{
                  singleAgendaGroupedByDay,
                  tracks,
                  trackById,
                  filterSessions,
                  isLoading,
                  goToStreamFromSession
                }"
              >
                <div>
                  <!-- Agenda panel: the hero picture + the agenda. Renders
                       inline on desktop (display: contents); on mobile it's
                       collapsed until the button opens it, then shown in place
                       on this same page (not a separate full-screen page). -->
                  <div
                    class="prog-agenda-panel"
                    :style="
                      agendaOverlayOpen ? { '--agenda-top': agendaTop } : null
                    "
                    :class="{
                      'prog-agenda-panel--open':
                        agendaOverlayOpen || !isAgendaEnabled,
                      'prog-agenda-panel--overlay':
                        agendaOverlayOpen && isAgendaEnabled
                    }"
                  >
                    <!-- In-page header with a close (✕) button — only when the
                         agenda is enabled (i.e. it's a real toggle). -->
                    <div v-if="isAgendaEnabled" class="prog-agenda-panel__head">
                      <h2 class="prog-agenda-panel__title">
                        {{ $t('Views.Program.agenda') }}
                      </h2>
                      <button
                        type="button"
                        class="prog-agenda-panel__close"
                        :aria-label="$t('Views.Program.closeAgenda')"
                        @click="closeAgendaOverlay()"
                      >
                        <i class="fas fa-times" aria-hidden="true"></i>
                      </button>
                    </div>

                    <div class="prog-agenda-panel__body">
                      <!-- ── Hero (background = admin-uploaded live video/image)
                 with floating greeting + My Programs pills overlaid ── -->
                      <div
                        class="prog-hero"
                        :class="{
                          'prog-hero--embed': heroIsEmbed,
                          'prog-hero--video': heroIsVideo,
                          'prog-hero--empty': !heroMediaUrl
                        }"
                      >
                        <iframe
                          v-if="heroIsEmbed && heroEmbedUrl"
                          class="prog-hero__media prog-hero__media--iframe"
                          :src="heroEmbedUrl"
                          frameborder="0"
                          allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                          allowfullscreen
                          :title="$t('Views.Program.eventLiveStream')"
                        ></iframe>
                        <video
                          v-else-if="heroIsVideo"
                          class="prog-hero__media"
                          :src="heroMediaUrl"
                          autoplay
                          loop
                          muted
                          playsinline
                          preload="auto"
                          aria-hidden="true"
                        ></video>
                        <img
                          v-else-if="heroMediaUrl"
                          class="prog-hero__media"
                          :src="heroMediaUrl"
                          alt=""
                          aria-hidden="true"
                        />

                        <!-- Floating greeting pill (top-left) -->
                        <div class="prog-hero__greeting">
                          <Avatar
                            :image="(getUser && getUser.Picture) || null"
                            :circle="true"
                            :name="welcomeName"
                            :alt="welcomeName"
                            :aria-label="welcomeName"
                            class="prog-hero__avatar"
                          />
                          <span class="prog-hero__hi">{{ welcomePrefix }}</span>
                          <span class="prog-hero__name">{{ welcomeName }}</span>
                          <img
                            class="prog-hero__wave"
                            src="https://fonts.gstatic.com/s/e/notoemoji/latest/1f44b/512.gif"
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                          />
                        </div>

                        <!-- Floating My Programs pill (top-right). Shown only
                         when the admin's "My agenda" (booking) toggle is on. -->
                        <button
                          v-if="isBookingsEnabled"
                          type="button"
                          class="prog-hero__cta"
                          @click="goToMyPrograms"
                        >
                          <i class="fas fa-star" aria-hidden="true"></i>
                          <span>{{ $t('Views.Program.myPrograms') }}</span>
                        </button>
                      </div>

                      <!-- Agenda body — shown only when the admin has the
                       agenda enabled for this platform (Web-app for
                       physical attendees, Digital for digital ones).
                       When off, the session cards are hidden. -->
                      <template v-if="isAgendaEnabled">
                        <!-- ── Filters: themed custom popovers (search + room + category + date) -->
                        <div class="prog-filters">
                          <!-- Search — left on desktop, first row on mobile -->
                          <div class="prog-filter prog-filter--search">
                            <i
                              class="fas fa-search prog-filter__search-icon"
                              aria-hidden="true"
                            ></i>
                            <input
                              v-model="searchText"
                              class="prog-filter__search"
                              :placeholder="
                                $t('Views.Program.searchPlaceholder')
                              "
                              :aria-label="$t('Views.Program.searchSessions')"
                              @input="onSearch(filterSessions)"
                            />
                          </div>

                          <!-- Track — only when the admin has more than one -->
                          <div
                            v-if="tracks && tracks.length > 1"
                            class="prog-filter prog-filter--first-pill"
                          >
                            <span class="prog-filter__label">{{
                              $t('Views.Program.room')
                            }}</span>
                            <button
                              type="button"
                              class="prog-filter__trigger"
                              :class="{
                                'prog-filter__trigger--open': roomPickerOpen
                              }"
                              @click.stop="toggleRoomPicker"
                              @keydown.escape="roomPickerOpen = false"
                              aria-haspopup="listbox"
                              :aria-expanded="roomPickerOpen ? 'true' : 'false'"
                              :aria-label="$t('Views.Program.filterByRoom')"
                            >
                              <span class="prog-filter__value">{{
                                selectedRoomLabel(tracks)
                              }}</span>
                              <i
                                class="fas fa-chevron-down prog-filter__chevron"
                                :class="{
                                  'prog-filter__chevron--open': roomPickerOpen
                                }"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <transition name="notif-fade">
                              <div
                                v-if="roomPickerOpen"
                                class="prog-filter__panel"
                                role="listbox"
                                :aria-label="$t('Views.Program.selectRoom')"
                                @click.stop
                              >
                                <button
                                  type="button"
                                  class="prog-filter__option"
                                  :class="{
                                    'prog-filter__option--active':
                                      !selectedTrackId
                                  }"
                                  role="option"
                                  :aria-selected="
                                    !selectedTrackId ? 'true' : 'false'
                                  "
                                  @click="
                                    selectedTrackId = null;
                                    roomPickerOpen = false;
                                  "
                                >
                                  {{ $t('Views.Program.allRooms') }}
                                  <i
                                    v-if="!selectedTrackId"
                                    class="fas fa-check prog-filter__option-check"
                                  ></i>
                                </button>
                                <button
                                  v-for="track in tracks"
                                  :key="track.id"
                                  type="button"
                                  class="prog-filter__option"
                                  :class="{
                                    'prog-filter__option--active':
                                      selectedTrackId === track.id
                                  }"
                                  role="option"
                                  :aria-selected="
                                    selectedTrackId === track.id
                                      ? 'true'
                                      : 'false'
                                  "
                                  @click="
                                    selectedTrackId = track.id;
                                    roomPickerOpen = false;
                                  "
                                >
                                  {{ track.title }}
                                  <i
                                    v-if="selectedTrackId === track.id"
                                    class="fas fa-check prog-filter__option-check"
                                  ></i>
                                </button>
                              </div>
                            </transition>
                          </div>

                          <!-- Category -->
                          <div
                            v-if="
                              allCategories(singleAgendaGroupedByDay).length
                            "
                            class="prog-filter prog-filter--category"
                            :class="{
                              'prog-filter--first-pill': !(
                                tracks && tracks.length > 1
                              )
                            }"
                          >
                            <span class="prog-filter__label">{{
                              $t('Views.Program.category')
                            }}</span>
                            <button
                              type="button"
                              class="prog-filter__trigger"
                              :class="{
                                'prog-filter__trigger--open': categoryPickerOpen
                              }"
                              @click.stop="toggleCategoryPicker"
                              @keydown.escape="categoryPickerOpen = false"
                              aria-haspopup="listbox"
                              :aria-expanded="
                                categoryPickerOpen ? 'true' : 'false'
                              "
                              :aria-label="$t('Views.Program.filterByCategory')"
                            >
                              <span class="prog-filter__value">{{
                                selectedCategoryLabel
                              }}</span>
                              <i
                                class="fas fa-chevron-down prog-filter__chevron"
                                :class="{
                                  'prog-filter__chevron--open':
                                    categoryPickerOpen
                                }"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <transition name="notif-fade">
                              <div
                                v-if="categoryPickerOpen"
                                class="prog-filter__panel"
                                role="listbox"
                                :aria-label="$t('Views.Program.selectCategory')"
                                @click.stop
                              >
                                <button
                                  type="button"
                                  class="prog-filter__option"
                                  :class="{
                                    'prog-filter__option--active':
                                      !selectedCategory
                                  }"
                                  role="option"
                                  :aria-selected="
                                    !selectedCategory ? 'true' : 'false'
                                  "
                                  @click="
                                    selectedCategory = null;
                                    categoryPickerOpen = false;
                                  "
                                >
                                  {{ $t('Views.Program.allCategories') }}
                                  <i
                                    v-if="!selectedCategory"
                                    class="fas fa-check prog-filter__option-check"
                                  ></i>
                                </button>
                                <button
                                  v-for="cat in allCategories(
                                    singleAgendaGroupedByDay
                                  )"
                                  :key="cat"
                                  type="button"
                                  class="prog-filter__option"
                                  :class="{
                                    'prog-filter__option--active':
                                      selectedCategory === cat
                                  }"
                                  role="option"
                                  :aria-selected="
                                    selectedCategory === cat ? 'true' : 'false'
                                  "
                                  @click="
                                    selectedCategory = cat;
                                    categoryPickerOpen = false;
                                  "
                                >
                                  {{ cat }}
                                  <i
                                    v-if="selectedCategory === cat"
                                    class="fas fa-check prog-filter__option-check"
                                  ></i>
                                </button>
                              </div>
                            </transition>
                          </div>

                          <!-- Date filter — mobile only (desktop uses the day-tabs row). -->
                          <div class="prog-filter prog-filter--date">
                            <span class="prog-filter__label">{{
                              $t('Views.Program.date')
                            }}</span>
                            <!-- Previous day -->
                            <button
                              type="button"
                              class="prog-date-nav"
                              :disabled="
                                !adjacentSessionDay(
                                  -1,
                                  singleAgendaGroupedByDay
                                )
                              "
                              @click.stop="
                                stepDay(-1, singleAgendaGroupedByDay)
                              "
                              :aria-label="$t('Views.Program.previousDay')"
                            >
                              <i
                                class="fas fa-chevron-left"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <button
                              type="button"
                              class="prog-filter__trigger"
                              :class="{
                                'prog-filter__trigger--open': datepickerOpen
                              }"
                              @click.stop="toggleDatePicker"
                              @keydown.escape="datepickerOpen = false"
                              aria-haspopup="listbox"
                              :aria-expanded="datepickerOpen ? 'true' : 'false'"
                              :aria-label="$t('Views.Program.filterByDate')"
                            >
                              <span class="prog-filter__value">{{
                                selectedDateLabel(singleAgendaGroupedByDay)
                              }}</span>
                              <i
                                class="fas fa-chevron-down prog-filter__chevron"
                                :class="{
                                  'prog-filter__chevron--open': datepickerOpen
                                }"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <!-- Next day -->
                            <button
                              type="button"
                              class="prog-date-nav"
                              :disabled="
                                !adjacentSessionDay(1, singleAgendaGroupedByDay)
                              "
                              @click.stop="stepDay(1, singleAgendaGroupedByDay)"
                              :aria-label="$t('Views.Program.nextDay')"
                            >
                              <i
                                class="fas fa-chevron-right"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <transition name="notif-fade">
                              <div
                                v-if="datepickerOpen"
                                class="prog-filter__panel"
                                role="listbox"
                                :aria-label="$t('Views.Program.selectDate')"
                                @click.stop
                              >
                                <button
                                  v-for="day in sessionDays(
                                    singleAgendaGroupedByDay
                                  )"
                                  :key="day.date"
                                  type="button"
                                  class="prog-filter__option"
                                  :class="{
                                    'prog-filter__option--active':
                                      selectedDate === day.date
                                  }"
                                  role="option"
                                  :aria-selected="
                                    selectedDate === day.date ? 'true' : 'false'
                                  "
                                  @click="
                                    selectedDate = day.date;
                                    datepickerOpen = false;
                                  "
                                >
                                  {{ dayLabel(day) }}
                                  <i
                                    v-if="selectedDate === day.date"
                                    class="fas fa-check prog-filter__option-check"
                                  ></i>
                                </button>
                              </div>
                            </transition>
                          </div>

                          <button
                            v-if="selectedTrackId || selectedCategory"
                            type="button"
                            class="prog-filter__clear"
                            @click="clearFilters"
                            :aria-label="$t('Views.Program.clearAllFilters')"
                          >
                            <i class="fas fa-times" aria-hidden="true"></i>
                            {{ $t('Views.Program.clear') }}
                          </button>

                          <!-- Download agenda as PDF -->
                          <button
                            type="button"
                            class="prog-filter__download"
                            :aria-busy="isDownloadingPdf ? 'true' : 'false'"
                            @click="handleDownloadAgenda"
                          >
                            <i
                              class="fas"
                              :class="
                                isDownloadingPdf
                                  ? 'fa-spinner fa-spin'
                                  : 'fa-download'
                              "
                              aria-hidden="true"
                            ></i>
                            {{ $t('Views.Stage.Agenda.downloadAgenda') }}
                          </button>
                        </div>
                        <p
                          v-if="downloadError"
                          class="prog-download-error"
                          role="alert"
                        >
                          {{ $t('Views.Stage.Agenda.downloadAgendaFailed') }}
                        </p>

                        <!-- ── Day tabs (desktop: horizontal scroll, mobile: dropdown) ── -->

                        <!-- Desktop tabs -->
                        <div class="prog-days prog-days--desktop">
                          <div
                            v-for="day in sessionDays(singleAgendaGroupedByDay)"
                            :key="day.date"
                            class="prog-day"
                            :class="{
                              'prog-day--active': selectedDate === day.date
                            }"
                            @click="selectedDate = day.date"
                          >
                            <span class="prog-day__num">{{ day.dayNum }}</span>
                            <div class="prog-day__right">
                              <span class="prog-day__month">{{
                                day.month
                              }}</span>
                              <span class="prog-day__name">{{
                                day.dayName
                              }}</span>
                              <span class="prog-day__count">{{
                                $tc(
                                  'Views.Program.daySessionCount',
                                  day.count,
                                  {
                                    count: day.count
                                  }
                                )
                              }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Mobile date selector (custom themed dropdown) -->
                        <div class="prog-date-picker">
                          <button
                            class="prog-date-picker__trigger"
                            @click.stop="datepickerOpen = !datepickerOpen"
                            @keydown.escape="datepickerOpen = false"
                            type="button"
                            aria-haspopup="listbox"
                            :aria-expanded="datepickerOpen ? 'true' : 'false'"
                            :aria-label="$t('Views.Program.selectEventDate')"
                          >
                            <div class="prog-date-picker__icon">
                              <i
                                class="far fa-calendar-alt"
                                aria-hidden="true"
                              ></i>
                            </div>
                            <div
                              class="prog-date-picker__selected"
                              v-if="selectedDayObj(singleAgendaGroupedByDay)"
                            >
                              <span class="prog-date-picker__day">{{
                                dayLabel(
                                  selectedDayObj(singleAgendaGroupedByDay)
                                )
                              }}</span>
                              <span class="prog-date-picker__count">{{
                                $tc(
                                  'Views.Program.daySessionCount',
                                  selectedDayObj(singleAgendaGroupedByDay)
                                    .count,
                                  {
                                    count: selectedDayObj(
                                      singleAgendaGroupedByDay
                                    ).count
                                  }
                                )
                              }}</span>
                            </div>
                            <div class="prog-date-picker__selected" v-else>
                              <span class="prog-date-picker__day">{{
                                $t('Views.Program.selectADate')
                              }}</span>
                            </div>
                            <i
                              class="fas fa-chevron-down prog-date-picker__chevron"
                              :class="{
                                'prog-date-picker__chevron--open':
                                  datepickerOpen
                              }"
                              aria-hidden="true"
                            ></i>
                          </button>

                          <transition name="notif-fade">
                            <div
                              v-if="datepickerOpen"
                              class="prog-date-picker__panel"
                              @click.stop
                            >
                              <div class="prog-date-picker__panel-header">
                                <span class="prog-date-picker__panel-title">{{
                                  $t('Views.Program.chooseADay')
                                }}</span>
                                <button
                                  class="prog-date-picker__panel-close"
                                  @click="datepickerOpen = false"
                                  type="button"
                                  :aria-label="$t('Views.Stage.Agenda.close')"
                                >
                                  <i class="fas fa-times"></i>
                                </button>
                              </div>
                              <div
                                class="prog-date-picker__options"
                                role="listbox"
                                :aria-label="$t('Views.Program.eventDates')"
                                @keydown.escape="datepickerOpen = false"
                              >
                                <button
                                  v-for="day in sessionDays(
                                    singleAgendaGroupedByDay
                                  )"
                                  :key="day.date"
                                  class="prog-date-picker__option"
                                  :class="{
                                    'prog-date-picker__option--active':
                                      selectedDate === day.date
                                  }"
                                  @click="
                                    selectedDate = day.date;
                                    datepickerOpen = false;
                                  "
                                  type="button"
                                  role="option"
                                  :aria-selected="
                                    selectedDate === day.date ? 'true' : 'false'
                                  "
                                >
                                  <div class="prog-date-picker__option-num">
                                    {{ day.dayNum }}
                                  </div>
                                  <div class="prog-date-picker__option-info">
                                    <span class="prog-date-picker__option-name"
                                      >{{ day.dayName }}, {{ day.month }}</span
                                    >
                                    <span
                                      class="prog-date-picker__option-count"
                                      >{{
                                        $tc(
                                          'Views.Program.daySessionCount',
                                          day.count,
                                          {
                                            count: day.count
                                          }
                                        )
                                      }}</span
                                    >
                                  </div>
                                  <i
                                    v-if="selectedDate === day.date"
                                    class="fas fa-check prog-date-picker__option-check"
                                  ></i>
                                </button>
                              </div>
                            </div>
                          </transition>
                        </div>

                        <!-- ── Sessions list ────────────────────────────────────────── -->
                        <div class="prog-sessions px-4 pb-8">
                          <div v-if="isLoading" class="prog-empty">
                            <i
                              class="fas fa-circle-notch fa-spin"
                              style="
                                font-size: 24px;
                                color: var(--color-primary, #047873);
                              "
                            ></i>
                          </div>

                          <template v-else>
                            <!-- Empty day — the date arrows can land on a
                             calendar day that has no sessions. -->
                            <div
                              v-if="
                                !displayedSessions(singleAgendaGroupedByDay)
                                  .length
                              "
                              class="prog-empty prog-empty--text"
                            >
                              {{ $t('Views.Program.noSessionsOnDay') }}
                            </div>
                            <div v-else class="session-grid">
                              <div
                                v-for="session in displayedSessions(
                                  singleAgendaGroupedByDay
                                )"
                                :key="session.id"
                                class="session-card"
                                :class="{
                                  'session-card--ended': isPassed(session)
                                }"
                              >
                                <!-- Left: time + status -->
                                <div class="session-card__time-col">
                                  <i
                                    class="far fa-clock session-card__clock"
                                    aria-hidden="true"
                                  ></i>
                                  <span class="session-card__start">{{
                                    formatTime(session.startDate)
                                  }}</span>
                                  <span class="session-card__end">{{
                                    formatTime(session.endDate)
                                  }}</span>
                                  <!-- Available / Ended tag — hidden for now (kept in
                                   the markup so it can be re-enabled). -->
                                  <span
                                    v-show="false"
                                    class="session-card__status"
                                    :class="
                                      getStatusClass(session, bookSessions)
                                    "
                                  >
                                    <i
                                      v-if="isLocked(session, bookSessions)"
                                      class="fas fa-lock"
                                      aria-hidden="true"
                                    ></i>
                                    {{ getStatusLabel(session, bookSessions) }}
                                  </span>
                                </div>

                                <!-- Right: info -->
                                <div class="session-card__body">
                                  <!-- Title row — title + inline category chips -->
                                  <div class="session-card__title-row">
                                    <h3 class="session-card__title">
                                      <span
                                        v-if="
                                          isLive(session) && !isPassed(session)
                                        "
                                        class="session-card__live-dot"
                                        role="img"
                                        :aria-label="
                                          $t('Views.Program.statusLive')
                                        "
                                        :title="$t('Views.Program.statusLive')"
                                      ></span>
                                      {{ session.title }}
                                    </h3>
                                    <span
                                      v-for="(cat, ci) in sessionCategories(
                                        session
                                      )"
                                      :key="
                                        'cat-' + ci + '-' + (cat.value || cat)
                                      "
                                      class="session-card__category-chip"
                                    >
                                      <i
                                        class="fas fa-tag"
                                        aria-hidden="true"
                                      ></i>
                                      {{ cat.value || cat.name || cat }}
                                    </span>
                                  </div>

                                  <!-- Subtitle -->
                                  <p
                                    v-if="session.subtitle"
                                    class="session-card__subtitle"
                                  >
                                    {{ session.subtitle }}
                                  </p>
                                  <p
                                    v-if="session.subtitle2"
                                    class="session-card__subtitle"
                                  >
                                    {{ session.subtitle2 }}
                                  </p>

                                  <div class="session-card__meta">
                                    <!-- Track + seat-availability are grouped in a
                           sub-row so they always stay on the SAME line
                           even when the meta wraps. Participants and
                           any other meta items follow on their own
                           rows below as the card narrows. -->
                                    <div class="session-card__meta-row">
                                      <div class="session-card__chip-pair">
                                        <span
                                          v-if="
                                            tracks &&
                                            tracks.length > 1 &&
                                            session.trackId &&
                                            session.trackId !== '0' &&
                                            trackById(session.trackId) &&
                                            !trackById(session.trackId)
                                              .hideTrackTitle
                                          "
                                          class="session-card__meta-item session-card__track-pill"
                                          :style="
                                            trackPillStyle(
                                              trackById(session.trackId)
                                            )
                                          "
                                        >
                                          <i
                                            class="fas fa-door-open"
                                            aria-hidden="true"
                                          ></i>
                                          {{ trackById(session.trackId).title }}
                                        </span>
                                        <span
                                          v-if="hasSeatLimit(session)"
                                          class="session-card__meta-item session-card__seats-pill"
                                          :class="seatClass(session)"
                                        >
                                          <i
                                            class="fas fa-chair"
                                            aria-hidden="true"
                                          ></i>
                                          {{ seatLabel(session) }}
                                        </span>
                                      </div>
                                      <!-- Participant count — hidden on the card (still
                                       shown in the details modal). -->
                                      <span
                                        v-show="false"
                                        class="session-card__meta-item"
                                      >
                                        <i
                                          class="fas fa-user-friends"
                                          aria-hidden="true"
                                        ></i>
                                        {{
                                          $tc(
                                            'Views.Program.participantCount',
                                            (session.userIds &&
                                              session.userIds.length) ||
                                              0,
                                            {
                                              count:
                                                (session.userIds &&
                                                  session.userIds.length) ||
                                                0
                                            }
                                          )
                                        }}
                                      </span>
                                    </div>
                                  </div>
                                  <div class="session-card__actions">
                                    <button
                                      v-if="!isLocked(session, bookSessions)"
                                      class="session-card__details-btn"
                                      @click="openSessionModal(session)"
                                    >
                                      {{ $t('Views.Program.viewDetails') }}
                                      <i
                                        class="fas fa-chevron-right"
                                        aria-hidden="true"
                                      ></i>
                                    </button>
                                    <SessionDocuments :session="session" />
                                    <span
                                      v-if="isPassed(session)"
                                      class="session-card__ended-label"
                                    >
                                      {{ $t('Views.Program.sessionEnded') }}
                                    </span>
                                  </div>
                                </div>

                                <!-- Open-session button (bottom-right) — on every
                                 session of a digital event, so the info and
                                 recordings are reachable; never on web-app /
                                 physical events. -->
                                <button
                                  v-if="!isOnWebapp && session.liveUrl"
                                  class="session-card__play-btn"
                                  :class="{
                                    'session-card__play-btn--live':
                                      isLive(session) && !isPassed(session)
                                  }"
                                  @click="
                                    joinSession(session, goToStreamFromSession)
                                  "
                                  :aria-label="
                                    isLive(session) && !isPassed(session)
                                      ? $t('Views.Program.joinSessionNow')
                                      : $t('Views.Program.openSession')
                                  "
                                  type="button"
                                >
                                  <i class="fas fa-play" aria-hidden="true"></i>
                                </button>

                                <!-- Expand/book toggle button -->
                                <button
                                  v-if="isBookingsEnabled && !isPassed(session)"
                                  class="session-card__toggle"
                                  :class="{
                                    'session-card__toggle--booked': isBooked(
                                      session,
                                      bookSessions
                                    )
                                  }"
                                  :disabled="isBookSessionLoading"
                                  @click="addOrDeleteBooksSesions(session)"
                                  :aria-label="
                                    isBooked(session, bookSessions)
                                      ? $t('Views.Program.removeFromMyProgram')
                                      : $t('Views.Program.addToMyProgram')
                                  "
                                >
                                  <i
                                    :class="
                                      isBooked(session, bookSessions)
                                        ? 'fas fa-minus'
                                        : 'fas fa-plus'
                                    "
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </div>
                            </div>

                            <div
                              v-if="
                                !displayedSessions(singleAgendaGroupedByDay)
                                  .length
                              "
                              class="prog-empty"
                            >
                              <i
                                class="far fa-calendar-times"
                                style="
                                  font-size: 32px;
                                  color: #d1d5db;
                                  margin-bottom: 12px;
                                "
                              ></i>
                              <p style="color: #6b7280; font-size: 14px">
                                {{ $t('Views.Program.noSessionsFound') }}
                              </p>
                            </div>
                          </template>
                        </div>
                      </template>
                    </div>
                    <!-- /.prog-agenda-panel__body -->
                  </div>
                  <!-- /.prog-agenda-panel -->

                  <!-- Agenda turned off for this platform from the admin
                       side — render nothing (intentionally blank, no
                       "unavailable" notice). -->

                  <!-- ── Session detail modal ─────────────────────────────────── -->
                  <div
                    v-if="activeSession"
                    class="session-modal-backdrop"
                    @click.self="activeSession = null"
                  >
                    <div class="session-modal">
                      <button
                        class="session-modal__close"
                        @click="activeSession = null"
                        :aria-label="$t('Views.Stage.Agenda.close')"
                      >
                        <i class="fas fa-times" aria-hidden="true"></i>
                      </button>
                      <div class="session-modal__header">
                        <span class="session-modal__time">
                          {{ formatTime(activeSession.startDate) }} –
                          {{ formatTime(activeSession.endDate) }}
                        </span>
                        <h2 class="session-modal__title">
                          {{ activeSession.title }}
                        </h2>
                        <p
                          v-if="activeSession.subtitle"
                          class="session-modal__subtitle"
                        >
                          {{ activeSession.subtitle }}
                        </p>
                        <p
                          v-if="activeSession.subtitle2"
                          class="session-modal__subtitle"
                        >
                          {{ activeSession.subtitle2 }}
                        </p>
                        <p
                          v-if="speakerNames(activeSession)"
                          class="session-modal__speaker"
                        >
                          <i
                            class="fas fa-microphone-alt"
                            aria-hidden="true"
                          ></i>
                          <template v-for="(sp, si) in activeSession.speakers">
                            <span :key="'msep-' + si" v-if="si > 0">, </span>
                            <button
                              :key="sp.id || sp.UserID || sp.name || si"
                              type="button"
                              class="session-modal__speaker-btn"
                              @click.stop="openSpeakerModal(sp)"
                              :aria-label="
                                $t('Views.Program.viewSpeakerDetails', {
                                  name: speakerFullName(sp)
                                })
                              "
                            >
                              {{ speakerFullName(sp) }}
                            </button>
                          </template>
                        </p>
                        <div class="session-modal__meta">
                          <span
                            v-if="
                              tracks &&
                              tracks.length > 1 &&
                              activeSession.trackId &&
                              activeSession.trackId !== '0' &&
                              trackById(activeSession.trackId) &&
                              !trackById(activeSession.trackId).hideTrackTitle
                            "
                            class="session-card__track-pill"
                            :style="
                              trackPillStyle(trackById(activeSession.trackId))
                            "
                          >
                            <i class="fas fa-door-open" aria-hidden="true"></i>
                            {{ trackById(activeSession.trackId).title }}
                          </span>
                          <span
                            v-if="hasSeatLimit(activeSession)"
                            class="session-card__seats-pill"
                            :class="seatClass(activeSession)"
                          >
                            <i class="fas fa-chair" aria-hidden="true"></i>
                            {{ seatLabel(activeSession) }}
                          </span>
                        </div>
                      </div>
                      <div
                        v-if="activeSession.description"
                        class="session-modal__description html-content"
                        v-html="activeSession.description"
                      />
                      <div class="session-modal__footer">
                        <button
                          v-if="!isOnWebapp && isLive(activeSession)"
                          class="session-modal__join-btn"
                          @click="
                            joinSession(activeSession, goToStreamFromSession)
                          "
                        >
                          <span class="session-card__join-dot"></span>
                          {{ $t('Views.Program.joinNow') }}
                        </button>
                        <button
                          v-if="isBookingsEnabled"
                          class="session-modal__book-btn"
                          :class="{
                            'session-modal__book-btn--booked': isBooked(
                              activeSession,
                              bookSessions
                            )
                          }"
                          @click="addOrDeleteBooksSesions(activeSession)"
                          :disabled="isBookSessionLoading"
                        >
                          <i
                            :class="
                              isBooked(activeSession, bookSessions)
                                ? 'fas fa-minus-circle'
                                : 'fas fa-plus-circle'
                            "
                            aria-hidden="true"
                          ></i>
                          {{
                            isBooked(activeSession, bookSessions)
                              ? $t('Views.Program.removeFromMyProgram')
                              : $t('Views.Program.addToMyProgram')
                          }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </AgendasDataProvider>
            </BookSessionsDataProvider>
          </div>

          <div v-else class="prog-empty" style="min-height: 60vh">
            <i
              class="fas fa-circle-notch fa-spin"
              style="font-size: 24px; color: var(--color-primary, #047873)"
            ></i>
          </div>
        </div>
      </CurrentEventDataProvider>
    </template>
  </LiveStream>
</template>

<script>
// Program is rendered as content inside LiveStream's `main` slot.
// LiveStream itself provides the Navbar + side panel + page chrome,
// so we no longer import Navbar / EventSidePanel here.
import LiveStream from '@/views/LiveStream';
import Avatar from '@/components/shared/Avatar';
import SessionDocuments from '@/components/shared/SessionDocuments';
import SpeakerInfoModal from '@/components/SpeakerInfoModal';
import {
  resolveParticipationType,
  setStoredParticipationType
} from '@/helpers/redirection-helpers';
import Vue from 'vue';
import ApiClient from '@/client';
import { mapGetters, mapActions } from 'vuex';
import {
  embeddableType,
  toEmbeddableUrl,
  getApplicablePlatforms,
  getPusherInstance
} from '@/utils';

export default {
  name: 'Program',
  components: { LiveStream, Avatar, SessionDocuments },
  data() {
    return {
      searchText: '',
      selectedDate: null,
      selectedTrackId: null,
      selectedCategory: null,
      activeSession: null,
      searchDebounce: null,
      datepickerOpen: false,
      roomPickerOpen: false,
      categoryPickerOpen: false,
      // Mobile only: whether the agenda (filters + day nav + sessions)
      // full-screen overlay is open. Ignored on desktop, where the
      // agenda always renders inline.
      agendaOverlayOpen: false,
      // Where the full-screen agenda starts: just below the (possibly
      // tall) header, measured from the DOM.
      agendaTop: '64px',
      // Tracks whether the hero image failed to load — switched to
      // `true` from the <img>'s @error handler so we don't render a
      // broken-image icon, and reset to `false` on @load.
      heroImageFailed: false,
      isDownloadingPdf: false,
      downloadError: false
    };
  },
  created() {
    // Restore live agenda updates: admin add/edit/update of sessions emits
    // `agenda-change` over the websocket. Re-fetch so the program refreshes
    // without a manual reload (mirrors the old Stage agenda + AgendaSection).
    getPusherInstance({ isReady: true }).bind('agenda-change', () => {
      this.refreshAgendas();
    });
  },
  computed: {
    ...mapGetters('CurrentEvent', { getEvent: 'getEvent' }),
    ...mapGetters('CurrentEvent', [
      'features',
      'isWebappEnabled',
      'isLobbyEnabled',
      'isStageEnabled'
    ]),
    ...mapGetters('Authentication', { getUser: 'getUser' }),
    ...mapGetters('Agendas', {
      getSessionById: 'getSessionById',
      getAllSessions: 'getSessions'
    }),
    // The admin "Main stage" toggle (header element's mainStageEnabled).
    // Mirrors Navbar.isMainStageShown: no lobby config → enabled.
    isMainStageEnabled() {
      const ev = this.getEvent || {};
      if (!ev.lobbyElements) return true;
      return !!ev.lobbyElements.find((x) => x && x.id === 'header')?.options
        ?.mainStageEnabled;
    },
    // ── Platform context (same detection as LiveStream.vue) ──────────
    // Physical attendees use the web-app surface; digital attendees use
    // the digital/stage surface. Drives which admin agenda toggle gates
    // this page.
    isOnWebapp() {
      return getApplicablePlatforms(this.$route).includes('webApp');
    },
    // The admin's AGENDA visibility toggle for the current platform:
    // "Web-app" (webAppNavigationAgenda) for physical attendees,
    // "Digital" (stageTabAgenda) for digital ones. When off, the
    // session cards are hidden on this page.
    isAgendaEnabled() {
      const f = this.features || {};
      return this.isOnWebapp ? !!f.webAppNavigationAgenda : !!f.stageTabAgenda;
    },
    // The admin's "My agenda" toggle (booking). When off, the "My
    // Programs" entry points and the per-session add/remove-from-program
    // controls are hidden — attendees can browse the agenda but not book.
    isBookingsEnabled() {
      return !!(this.features || {}).bookingsEnabled;
    },
    // Mirror Agenda.vue: agendaTimezoneMode & timezone from event
    agendaTimezoneMode() {
      return this.getEvent?.agendaTimezoneMode;
    },
    eventTimezone() {
      return this.getEvent?.timezone;
    },
    selectedCategoryLabel() {
      return this.selectedCategory || this.$t('Views.Program.allCategories');
    },
    // Logged-in attendee's display name for the welcome strip.
    welcomeName() {
      const u = this.getUser || {};
      const first = u.Firstname || u.firstName || u.FirstName || '';
      const last = u.Lastname || u.lastName || u.LastName || '';
      return (
        `${first} ${last}`.trim() ||
        this.$t('Views.Program.welcomeFallbackName')
      );
    },
    // Time-of-day prefix so the strip reads warmer than a cold "Welcome".
    welcomePrefix() {
      const hour = new Date().getHours();
      if (hour < 5) return this.$t('Views.Program.goodEvening');
      if (hour < 12) return this.$t('Views.Program.goodMorning');
      if (hour < 17) return this.$t('Views.Program.goodAfternoon');
      if (hour < 21) return this.$t('Views.Program.goodEvening');
      return this.$t('Views.Program.welcomeBack');
    },
    // Hero background is whatever the admin set as the event's
    // "Live video/image URL" — falls through a few common field
    // names since payloads vary by event configuration.
    heroMediaUrl() {
      const e = this.getEvent || {};
      return (
        e.streamUrl ||
        e.liveUrl ||
        e.liveImageUrl ||
        e.liveVideoUrl ||
        e.streamImageUrl ||
        ''
      );
    },
    // Heuristic: treat URL as a direct video file when it ends with
    // a video file extension. Streaming embeds (YouTube/Vimeo/HLS/etc.)
    // are detected separately and rendered through an iframe.
    heroIsVideo() {
      const url = (this.heroMediaUrl || '').split('?')[0].toLowerCase();
      return /\.(mp4|webm|mov|ogv|m4v)$/.test(url);
    },
    // Streaming embed (YouTube, Vimeo, Whereby, IVS, m3u8) — anything
    // `embeddableType` recognises that isn't a direct video file.
    heroEmbedKind() {
      if (!this.heroMediaUrl) return 'none';
      try {
        return embeddableType(this.heroMediaUrl);
      } catch (e) {
        return 'none';
      }
    },
    heroIsEmbed() {
      const kind = this.heroEmbedKind;
      return kind && kind !== 'none' && kind !== 'uknown';
    },
    // Convert the admin URL into an embed URL via the platform helper,
    // then layer on autoplay/mute/loop params for YouTube and Vimeo. The
    // banner is the event's main stream, so the player controls stay
    // visible: viewers can unmute, pause and go fullscreen.
    heroEmbedUrl() {
      if (!this.heroIsEmbed) return '';
      let url = '';
      try {
        url = toEmbeddableUrl(this.heroMediaUrl) || '';
      } catch (e) {
        url = '';
      }
      if (!url) return '';
      const kind = this.heroEmbedKind;
      try {
        const u = new URL(url, window.location.origin);
        if (kind === 'youtube' || kind === 'youtube-embed') {
          u.searchParams.set('autoplay', '1');
          u.searchParams.set('mute', '1');
          u.searchParams.set('loop', '1');
          u.searchParams.set('modestbranding', '1');
          u.searchParams.set('rel', '0');
          u.searchParams.set('playsinline', '1');
          // YouTube needs `playlist=<id>` to honour `loop=1`.
          const m = u.pathname.match(/\/embed\/([^/?]+)/);
          if (m && m[1] && !u.searchParams.has('playlist')) {
            u.searchParams.set('playlist', m[1]);
          }
        } else if (kind === 'vimeo' || kind === 'vimeo-embed') {
          u.searchParams.set('autoplay', '1');
          u.searchParams.set('muted', '1');
          u.searchParams.set('loop', '1');
        }
        return u.toString();
      } catch (e) {
        return url;
      }
    }
  },
  methods: {
    // Re-fetch the agenda (bypasses the DataProvider's items.length lock)
    // when a live `agenda-change` event arrives.
    ...mapActions('Agendas', { refreshAgendas: 'refresh' }),
    // ── Time formatting — identical to Agenda.vue formatAgendaTime ────────
    formatTime(dateStr) {
      if (!dateStr) return '';
      if (this.agendaTimezoneMode === 2) {
        return new Intl.DateTimeFormat('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: this.eventTimezone
        }).format(new Date(dateStr));
      }
      return this.$moment.utc(dateStr).local().format('HH:mm');
    },

    speakerNames(session) {
      if (!session.speakers || !session.speakers.length) return '';
      return session.speakers
        .map((s) => `${s.name || ''} ${s.lastName || ''}`.trim())
        .filter(Boolean)
        .join(', ');
    },
    speakerFullName(sp) {
      if (!sp) return '';
      return `${sp.name || ''}${sp.lastName ? ' ' + sp.lastName : ''}`.trim();
    },
    sessionCategories(session) {
      // Admin-added categories. Accept array-of-objects with `value`,
      // array-of-strings, or a singular `category` field for
      // forwards/backwards compatibility across backend versions.
      if (!session) return [];
      const raw = session.categories || session.Categories || session.category;
      if (!raw) return [];
      const list = Array.isArray(raw) ? raw : [raw];
      return list.filter((c) => {
        if (!c) return false;
        if (typeof c === 'string') return c.trim().length > 0;
        const label = c.value || c.name || c.label || c.title;
        return label && String(label).trim().length > 0;
      });
    },
    handleDownloadAgenda() {
      if (this.isDownloadingPdf) return;
      this.isDownloadingPdf = true;
      this.downloadError = false;
      ApiClient.exportAgendaPdf(false)
        .catch(() => {
          this.downloadError = true;
        })
        .finally(() => {
          this.isDownloadingPdf = false;
        });
    },
    clearFilters() {
      this.selectedTrackId = null;
      this.selectedCategory = null;
    },
    // Custom popovers: exclusively toggle so only one panel is open
    // at a time (matches the date picker's open/close behaviour).
    toggleRoomPicker() {
      this.roomPickerOpen = !this.roomPickerOpen;
      this.categoryPickerOpen = false;
      this.datepickerOpen = false;
    },
    toggleCategoryPicker() {
      this.categoryPickerOpen = !this.categoryPickerOpen;
      this.roomPickerOpen = false;
      this.datepickerOpen = false;
    },
    toggleDatePicker() {
      this.datepickerOpen = !this.datepickerOpen;
      this.roomPickerOpen = false;
      this.categoryPickerOpen = false;
    },
    selectedRoomLabel(tracks) {
      if (!this.selectedTrackId) return this.$t('Views.Program.allRooms');
      const list = Array.isArray(tracks) ? tracks : [];
      const t = list.find((x) => x && x.id === this.selectedTrackId);
      return t && t.title ? t.title : this.$t('Views.Program.allRooms');
    },
    selectedDateLabel(singleAgendaGroupedByDay) {
      const days = this.sessionDays(singleAgendaGroupedByDay);
      const current = this.selectedDate || (days[0] && days[0].date) || null;
      if (!current) return this.$t('Views.Program.selectADate');
      // Format the actual selected date so the label stays correct even on
      // a day with no sessions — the prev/next arrows can step across months
      // and years, so the year is shown to make the selection unambiguous.
      const m = Vue.moment(current, 'YYYY-MM-DD');
      return this.$t('Views.Program.fullDateLabel', {
        dayName: m.format('dddd'),
        month: m.format('MMM').toUpperCase(),
        dayNum: m.format('D'),
        year: m.format('YYYY')
      });
    },
    // "Monday, JAN 5" — word order comes from the translation.
    dayLabel(day) {
      if (!day) return '';
      return this.$t('Views.Program.dayLabel', {
        dayName: day.dayName,
        month: day.month,
        dayNum: day.dayNum
      });
    },
    // The previous / next day that actually has sessions (mobile arrows),
    // so the arrows never land on an empty day. null when there is none.
    adjacentSessionDay(delta, singleAgendaGroupedByDay) {
      const days = this.sessionDays(singleAgendaGroupedByDay);
      const current = this.selectedDate || (days[0] && days[0].date) || null;
      if (!current) return null;
      const found =
        delta > 0
          ? days.find((d) => d.date > current)
          : days
              .slice()
              .reverse()
              .find((d) => d.date < current);
      return found ? found.date : null;
    },
    stepDay(delta, singleAgendaGroupedByDay) {
      const next = this.adjacentSessionDay(delta, singleAgendaGroupedByDay);
      if (next) this.selectedDate = next;
    },
    // Build a { background-color, color } style for the track pill.
    // Uses the admin-configured headerBackgroundColor as the pill bg,
    // and picks black or white text based on the background's relative
    // luminance so the title stays readable regardless of the color
    // the admin chose.
    trackPillStyle(track) {
      const bg = (track && track.headerBackgroundColor) || '';
      if (!bg) return null;
      return {
        backgroundColor: bg,
        color: this.pickContrastingTextColor(bg),
        borderColor: bg
      };
    },
    pickContrastingTextColor(bg) {
      const hex = String(bg).trim().replace(/^#/, '');
      const full =
        hex.length === 3
          ? hex
              .split('')
              .map((c) => c + c)
              .join('')
          : hex.length === 6
          ? hex
          : null;
      if (!full) return '#ffffff';
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      // Per WCAG relative-luminance: dark text on light bg, white on dark.
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return lum > 0.62 ? '#111827' : '#ffffff';
    },
    // Normalize a category entry (string or object) down to its label.
    categoryLabel(cat) {
      if (!cat) return '';
      if (typeof cat === 'string') return cat.trim();
      return String(
        cat.value || cat.name || cat.label || cat.title || ''
      ).trim();
    },
    // Unique, alphabetized list of categories across every session in
    // the agenda — drives the "Filter by category" pill row.
    allCategories(singleAgendaGroupedByDay) {
      const sessions = singleAgendaGroupedByDay?.sessions || {};
      const seen = new Set();
      Object.values(sessions).forEach((day) => {
        (day || []).forEach((s) => {
          this.sessionCategories(s).forEach((c) => {
            const label = this.categoryLabel(c);
            if (label) seen.add(label);
          });
        });
      });
      return Array.from(seen).sort((a, b) => a.localeCompare(b));
    },
    openSpeakerModal(speaker) {
      if (!speaker) return;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        SpeakerInfoModal,
        { speaker },
        {
          width: '92%',
          height: 'auto',
          adaptive: true,
          minWidth: 300,
          maxWidth: 640
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },

    // ── Seat availability helpers ────────────────────────────────
    hasSeatLimit(session) {
      return !!(session && session.myAgendaSize && session.myAgendaSize > 0);
    },
    seatsBookedCount(session) {
      return (session && session.userIds && session.userIds.length) || 0;
    },
    seatsLeftCount(session) {
      return Math.max(
        0,
        (session.myAgendaSize || 0) - this.seatsBookedCount(session)
      );
    },
    seatLabel(session) {
      const left = this.seatsLeftCount(session);
      const total = session.myAgendaSize || 0;
      if (left <= 0) return this.$t('Views.Program.seatsFull');
      return this.$tc('Views.Program.seatsLeft', left, { left, total });
    },
    seatClass(session) {
      const left = this.seatsLeftCount(session);
      const total = session.myAgendaSize || 0;
      if (left <= 0) return 'session-card__seats--full';
      if (total > 0 && left / total <= 0.2) return 'session-card__seats--low';
      return 'session-card__seats--ok';
    },

    // ── Booking state — mirrors Agenda.vue logic exactly ─────────────────
    isBooked(session, bookSessions) {
      return bookSessions && bookSessions.some((b) => b.id === session.id);
    },
    // Locked = capacity reached AND current user hasn't booked
    isLocked(session, bookSessions) {
      if (!session.myAgendaSize || session.myAgendaSize === 0) return false;
      const currentUserBooked =
        session.userIds &&
        session.userIds.some((id) => id === this.getUser?.UserID);
      return (
        !currentUserBooked &&
        session.userIds &&
        session.userIds.length >= session.myAgendaSize
      );
    },
    // Parse session datetime the same way Agenda.vue's
    // formatAgendaTime does for time-of-day formatting (dynamic mode
    // = UTC → user's local; static mode = event's configured tz).
    // Used only for displaying the time label; comparisons against
    // "now" use the raw moment(date) so any explicit offset on the
    // API string is honoured natively.
    parseSessionDate(date) {
      if (!date) return null;
      if (
        this.agendaTimezoneMode === 2 &&
        this.eventTimezone &&
        this.$moment.tz
      ) {
        return this.$moment.tz(date, this.eventTimezone);
      }
      return this.$moment.utc(date).local();
    },
    // Resolve a session datetime to an absolute moment that we can
    // safely compare against `now`. The API often returns naive
    // datetimes meant to be in the event's configured timezone
    // (e.g. Europe/Oslo) — `$moment(naive)` would otherwise
    // misinterpret those as the user's local timezone (e.g. IST).
    sessionMoment(date) {
      if (!date) return null;
      const hasOffset = /Z|[+-]\d{2}:?\d{2}$/.test(String(date));
      // Already carries an explicit UTC marker / offset — moment
      // handles it natively.
      if (hasOffset) return this.$moment(date);
      // Naive datetime + event timezone configured → interpret in
      // the event's timezone so an Oslo-set 06:30 reads as 06:30
      // Oslo no matter where the viewer is.
      if (this.eventTimezone && this.$moment.tz) {
        return this.$moment.tz(date, this.eventTimezone);
      }
      // Fallback: let moment do its default parse.
      return this.$moment(date);
    },
    // Passed — true once the session end time is in the past.
    isPassed(session) {
      if (!session || !session.endDate) return false;
      const end = this.sessionMoment(session.endDate);
      return end && end.isBefore(this.$moment());
    },
    // Live — current time falls between session start and end.
    isLive(session) {
      if (!session || !session.startDate || !session.endDate) return false;
      const now = this.$moment();
      const start = this.sessionMoment(session.startDate);
      const end = this.sessionMoment(session.endDate);
      if (!start || !end) return false;
      return now.isSameOrAfter(start) && now.isBefore(end);
    },

    getStatusLabel(session, bookSessions) {
      if (this.isBooked(session, bookSessions)) {
        return this.$t('Views.Program.statusAttending');
      }
      if (this.isPassed(session)) return this.$t('Views.Program.statusEnded');
      if (this.isLocked(session, bookSessions)) {
        return this.$t('Views.Program.statusLocked');
      }
      return this.$t('Views.Program.statusAvailable');
    },
    getStatusClass(session, bookSessions) {
      if (this.isBooked(session, bookSessions))
        return 'session-card__status--attending';
      if (this.isPassed(session)) return 'session-card__status--ended';
      if (this.isLocked(session, bookSessions))
        return 'session-card__status--locked';
      return 'session-card__status--available';
    },

    // ── Day tabs — only dates that have sessions (matches Agenda.vue) ─────
    sessionDays(singleAgendaGroupedByDay) {
      const sessions = singleAgendaGroupedByDay?.sessions || {};
      return Object.keys(sessions)
        .sort()
        .map((date) => ({
          date,
          dayNum: Vue.moment(date).format('D'),
          month: Vue.moment(date).format('MMM').toUpperCase(),
          dayName: Vue.moment(date).format('dddd'),
          count: sessions[date].length
        }));
    },
    selectedDayObj(singleAgendaGroupedByDay) {
      const days = this.sessionDays(singleAgendaGroupedByDay);
      return days.find((d) => d.date === this.selectedDate) || days[0] || null;
    },
    totalSessions(singleAgendaGroupedByDay) {
      const sessions = singleAgendaGroupedByDay?.sessions || {};
      return Object.values(sessions).reduce((sum, arr) => sum + arr.length, 0);
    },
    totalDays(singleAgendaGroupedByDay) {
      return Object.keys(singleAgendaGroupedByDay?.sessions || {}).length;
    },
    // ── Filtered sessions ─────────────────────────────────────────────────
    displayedSessions(singleAgendaGroupedByDay) {
      const sessions = singleAgendaGroupedByDay?.sessions || {};
      const days = Object.keys(sessions).sort();

      // Auto-select first day if none chosen
      const date = this.selectedDate || days[0] || null;
      if (!date) return [];

      if (!this.selectedDate && date) {
        this.$nextTick(() => {
          // Only fill in the default if nothing has chosen a day in the
          // meantime (e.g. the "back from live stream" restore).
          if (!this.selectedDate) this.selectedDate = date;
        });
      }

      let list = (sessions[date] || []).slice();

      // Track/room filter
      if (this.selectedTrackId) {
        list = list.filter((s) => s.trackId === this.selectedTrackId);
      }

      // Category filter
      if (this.selectedCategory) {
        list = list.filter((s) =>
          this.sessionCategories(s)
            .map((c) => this.categoryLabel(c))
            .includes(this.selectedCategory)
        );
      }

      // Search filter
      if (this.searchText.trim()) {
        const q = this.searchText.toLowerCase();
        list = list.filter((s) => {
          const inTitle = (s.title || '').toLowerCase().includes(q);
          const inSubtitle = (s.subtitle || '').toLowerCase().includes(q);
          const inSpeaker = this.speakerNames(s).toLowerCase().includes(q);
          const inKeywords = (s.keywords || []).some((k) =>
            (k.value || '').toLowerCase().includes(q)
          );
          return inTitle || inSubtitle || inSpeaker || inKeywords;
        });
      }

      // Sort by start time — same ordering as Agenda.vue collapsible groups
      return list.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    },

    // ── Search — debounced, calls Agendas/filterSessions store action ─────
    onSearch(filterSessions) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        filterSessions({ searchText: this.searchText, category: '' });
      }, 300);
    },

    // ── Session modal ─────────────────────────────────────────────────────
    openSessionModal(session) {
      this.activeSession = session;
      document.body.classList.add('overflow-hidden');
    },

    // ── Open one session's "View details" popup by id — used by the agenda
    //    buttons the admin inserts into promoted messages / info pages. The
    //    agenda and the feature toggles load asynchronously (and on a cold
    //    navigation after mount), so the id is held until both are ready. ──
    openSessionById(id) {
      this._pendingSessionId = id ? String(id) : null;
      this.applyPendingSession();
    },
    applyPendingSession() {
      const id = this._pendingSessionId;
      if (!id || !this.isAgendaEnabled) return;
      const session = this.getSessionById(id);
      if (!session) return;
      this._pendingSessionId = null;
      this.openSessionModal(session);
      // Drop the one-shot query so a refresh / back doesn't reopen the popup.
      if (this.$route.query.sessionDetails) {
        const { sessionDetails, ...query } = this.$route.query;
        this.$router.replace({ query }).catch(() => {});
      }
    },

    // ── Navigate to Profile → My Programs section ─────────────────────────
    goToMyPrograms() {
      this.$router.push({ path: '/profile', hash: '#my-programs' });
    },

    // ── Mobile agenda: shown in place on this page (open via the
    //    "Agendas" button, close via the header ✕). It's an in-page
    //    expander, not a full-screen overlay, so the page scrolls
    //    normally and no scroll lock is needed. ─────────────────────────
    openAgendaOverlay() {
      if (!this.agendaOverlayOpen) {
        // Remember where the user was so closing returns them there.
        this._agendaScrollY = window.pageYOffset || 0;
      }
      // Measure from the top of the page, where the navbar is in view.
      window.scrollTo({ top: 0 });
      this.syncAgendaTop();
      this.agendaOverlayOpen = true;
      document.body.classList.add('overflow-hidden');
      this.$root.$emit('program:agenda-state', true);
      // Re-measure once layout/fonts settle.
      this.$nextTick(this.syncAgendaTop);
      setTimeout(this.syncAgendaTop, 400);
      // Arriving from another page (e.g. a promoted-message Agenda button),
      // the navbar / event-name row may not be laid out yet, so the first
      // measurement is too small and the agenda header ends up behind the
      // navbar. Keep re-measuring while the page settles.
      clearInterval(this._agendaSyncTimer);
      let ticks = 0;
      this._agendaSyncTimer = setInterval(() => {
        this.syncAgendaTop();
        ticks += 1;
        if (ticks >= 20 || !this.agendaOverlayOpen) {
          clearInterval(this._agendaSyncTimer);
        }
      }, 150);
      // Always start at the top of the agenda.
      this.$nextTick(() => {
        const el = this.agendaScroller();
        if (el) el.scrollTop = 0;
      });
    },
    closeAgendaOverlay(stay) {
      this.agendaOverlayOpen = false;
      document.body.classList.remove('overflow-hidden');
      this.$root.$emit('program:agenda-state', false);
      // Close any open filter popovers so they don't linger next time.
      this.datepickerOpen = false;
      this.roomPickerOpen = false;
      this.categoryPickerOpen = false;
      // Opened from another page (bottom-nav Agenda → /program?agenda=1):
      // go back to wherever the user was.
      if (stay === true) {
        // Another bottom-menu item took over (Posts, Main stage…): stay on
        // this page, at the top, where the side panel / page content is.
        this._openedViaNav = false;
        this.$nextTick(() => window.scrollTo({ top: 0 }));
        return;
      }
      if (this._openedViaNav) {
        this._openedViaNav = false;
        if (window.history.length > 1) {
          this.$router.go(-1);
        } else {
          this.$router.replace({ name: 'program' }).catch(() => {});
        }
        return;
      }
      // Already on the page: return to the scroll position from before.
      const y = this._agendaScrollY || 0;
      this.$nextTick(() => window.scrollTo({ top: y }));
    },
    // Bottom edge of the top header, including a wrapped event title that
    // spills past the bar's fixed height.
    syncAgendaTop() {
      // The shell holds the bar AND the event name shown beneath it.
      const bar =
        document.querySelector('.navbar-shell') ||
        document.querySelector('.navbar');
      if (!bar) return;
      let bottom = bar.getBoundingClientRect().bottom;
      Array.prototype.forEach.call(bar.children, (c) => {
        const cls = c.classList;
        if (
          cls &&
          (cls.contains('bottom-nav') ||
            cls.contains('mobile-menu') ||
            cls.contains('mobile-chat-panel') ||
            cls.contains('skip-nav-link'))
        ) {
          return;
        }
        const r = c.getBoundingClientRect();
        if (r.height > 0) bottom = Math.max(bottom, r.bottom);
      });
      // Never negative: a navbar scrolled out of view must not push the
      // agenda's own header (title + close) off the top of the screen.
      this.agendaTop = `${Math.max(0, Math.ceil(bottom))}px`;
    },
    // The scrolling element of the agenda overlay (mobile). On desktop the
    // agenda is inline and the window scrolls, so there's no scroller.
    agendaScroller() {
      const panel = document.querySelector('.prog-agenda-panel--overlay');
      if (!panel || window.getComputedStyle(panel).position !== 'fixed') {
        return null;
      }
      return panel.querySelector('.prog-agenda-panel__body');
    },

    // ── Join a live session — sets the stream then routes to /live-stream ─
    onDocClickDatepicker() {
      // Close every filter popover when clicking outside them
      if (this.datepickerOpen) this.datepickerOpen = false;
      if (this.roomPickerOpen) this.roomPickerOpen = false;
      if (this.categoryPickerOpen) this.categoryPickerOpen = false;
    },
    joinSession(session, goToStreamFromSession) {
      // Set the stream URL/title in Vuex first if there's a liveUrl
      if (
        session &&
        session.liveUrl &&
        typeof goToStreamFromSession === 'function'
      ) {
        // goToStreamFromSession also navigates to /stage, so call setStream then route
        // Manually set instead of using the action
      }
      // Persist current session in the Stream store so the live page picks it up
      if (session) {
        this.$store.commit('Stream/setStreamUrl', session.liveUrl || '');
        this.$store.commit('Stream/setSessionId', session.id);
        this.$store.commit('Stream/setStreamTitle', session.title || '');
      }
      // Remember the agenda state so the browser/back button returns to it.
      try {
        sessionStorage.setItem(
          'tappin_program_return',
          JSON.stringify({
            open: this.agendaOverlayOpen,
            y: this.agendaScroller()
              ? this.agendaScroller().scrollTop
              : window.pageYOffset || 0,
            closeY: this._agendaScrollY || 0,
            date: this.selectedDate,
            // Digital / physical at the moment of leaving — re-asserted on
            // return so the round trip can't flip the attendee's platform.
            participation: resolveParticipationType()
          })
        );
      } catch (e) {
        /* storage unavailable — back just lands on the page top */
      }
      this.$router.push({
        name: 'live-stream',
        query: { session: session && session.id }
      });
    },
    // Coming back from the live-stream page (via a session's Join button):
    // reopen the agenda on the same day and scroll to where the user was.
    // The saved state is held in memory and applied once the event features
    // have loaded — at mount they often haven't, so applying immediately
    // would skip the agenda and lose the state.
    restoreAgendaState() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem('tappin_program_return'));
        sessionStorage.removeItem('tappin_program_return');
      } catch (e) {
        saved = null;
      }
      this._pendingReturn = saved || null;
      this.applyPendingReturn();
    },
    applyPendingReturn() {
      const saved = this._pendingReturn;
      if (!saved) return;
      // Wait for the event features (agenda toggle) and the signed-in user.
      if (!Object.keys(this.features || {}).length) return;
      const uid = this.getUser && this.getUser.UserID;
      if (!uid) return;
      this._pendingReturn = null;
      // Keep the attendee on the platform they left from (digital stays
      // digital) — before anything below reads the agenda/physical flags.
      if (
        saved.participation &&
        resolveParticipationType() !== saved.participation
      ) {
        setStoredParticipationType(uid, saved.participation);
      }
      if (saved.date) this.selectedDate = saved.date;
      if (saved.open && this.isAgendaEnabled) {
        this._agendaScrollY = saved.closeY || 0;
        this.agendaOverlayOpen = true;
        document.body.classList.add('overflow-hidden');
        this.$root.$emit('program:agenda-state', true);
      }
      // The sessions render asynchronously — keep re-applying the scroll
      // position until the page is tall enough to reach it (max ~4s).
      const y = saved.y || 0;
      let tries = 0;
      const tick = () => {
        const scroller = this.agendaScroller();
        if (scroller) scroller.scrollTop = y;
        else window.scrollTo({ top: y });
        const current = scroller ? scroller.scrollTop : window.pageYOffset || 0;
        const reached = Math.abs(current - y) < 4;
        tries += 1;
        if (!reached && tries < 40) setTimeout(tick, 100);
      };
      this.$nextTick(tick);
    },
    // When the admin "Main stage" toggle is off, the Program page must
    // not be accessible. Redirect to the lobby (event info), or the
    // live-stream page when the lobby is disabled.
    enforceMainStagePage() {
      if (this.$route.name !== 'program') return;
      // The bottom-nav Agenda / Posts / menu items open from this route even
      // when the Main stage page itself is switched off.
      if (this.$route.query.agenda || this.$route.query.tab) return;
      const ev = this.getEvent;
      // Wait until event details (lobbyElements) are loaded.
      if (!ev || !ev.lobbyElements) return;
      if (!this.isMainStageEnabled) {
        this.$router
          .replace({ name: this.isLobbyEnabled ? 'lobby' : 'live-stream' })
          .catch(() => {});
      }
    }
  },
  mounted() {
    // Switch the Program page off when admin has the "Main stage"
    // toggle off — redirect away from this route.
    this.enforceMainStagePage();
    document.addEventListener('click', this.onDocClickDatepicker);
    window.addEventListener('resize', this.syncAgendaTop);
    // Mobile bottom-nav "Agenda" opens the panel (also via ?agenda=1 when
    // the user came from another page).
    this._onOpenAgenda = () => {
      if (this.isAgendaEnabled) this.openAgendaOverlay();
    };
    this.$root.$on('program:open-agenda', this._onOpenAgenda);
    // Posts / other bottom-menu items must not stay hidden behind the agenda.
    this._onCloseAgenda = () => {
      if (this.agendaOverlayOpen) this.closeAgendaOverlay(true);
    };
    this.$root.$on('program:close-agenda', this._onCloseAgenda);
    this.$root.$on('live-stream:set-tab', this._onCloseAgenda);
    this.restoreAgendaState();
    if (this.$route.query.agenda) {
      this._openedViaNav = true;
      this.$nextTick(() => this._onOpenAgenda());
    }
    // Agenda buttons in messages / info pages (see helpers/agenda-link-helper).
    this.$root.$on('program:open-session', this.openSessionById);
    if (this.$route.query.sessionDetails) {
      this.openSessionById(this.$route.query.sessionDetails);
    }
  },
  beforeDestroy() {
    document.body.classList.remove('overflow-hidden');
    clearInterval(this._agendaSyncTimer);
    document.removeEventListener('click', this.onDocClickDatepicker);
    window.removeEventListener('resize', this.syncAgendaTop);
    this.$root.$off('program:open-session', this.openSessionById);
    if (this._onOpenAgenda) {
      this.$root.$off('program:open-agenda', this._onOpenAgenda);
    }
    if (this._onCloseAgenda) {
      this.$root.$off('program:close-agenda', this._onCloseAgenda);
      this.$root.$off('live-stream:set-tab', this._onCloseAgenda);
    }
    this.$root.$emit('program:agenda-state', false);
  },
  watch: {
    activeSession(val) {
      if (!val) document.body.classList.remove('overflow-hidden');
    },
    // Re-check once event details arrive after mount, and whenever the
    // toggle changes (admin may flip it live).
    isMainStageEnabled() {
      this.enforceMainStagePage();
    },
    // Event features arrive after mount — apply a pending "back from
    // live stream" restore as soon as they do.
    features() {
      this.applyPendingReturn();
      this.applyPendingSession();
    },
    getAllSessions() {
      this.applyPendingSession();
    },
    getUser() {
      this.applyPendingReturn();
    },
    // Reset the failed flag whenever the source URL changes so a
    // newly-uploaded image gets a fresh chance to load.
    heroMediaUrl() {
      this.heroImageFailed = false;
    }
  }
};
</script>

<style scoped lang="scss">
// Embedded mode — Program now renders inside LiveStream's `main`
// slot. LiveStream already provides the page wrapper, viewport
// height management and Navbar, so the existing `.program-page`
// constraints below would conflict (double viewport caps, double
// backgrounds). Reset them when in embedded mode.
.program-page--embedded {
  background: transparent !important;
  min-height: 0 !important;
  height: auto !important;
  overflow: visible !important;
  display: block !important;
  padding: 0 !important;

  ::v-deep #main-content,
  ::v-deep #main-content > * {
    flex: initial !important;
    min-height: 0 !important;
    overflow: visible !important;
    display: block !important;
  }

  // .prog-sessions originally became the only scrollable region
  // (flex: 1 1 0 + overflow-y: auto) so the page itself didn't
  // scroll. In embedded mode the document scrolls naturally — let
  // the sessions list grow to its full natural height.
  ::v-deep .prog-sessions {
    flex: initial !important;
    min-height: 0 !important;
    overflow-y: visible !important;
    overflow: visible !important;
  }
}

// Focus-visible indicator for keyboard users
// ── Non-scrollable page on desktop ───────────────────────────────
// Cap the program page at one viewport so the page-level scroll
// disappears. Inside, only the session list scrolls; everything
// else (hero, filters, day tabs) sits at its natural height.
// Mobile (≤1023px) reverts to natural document scrolling because
// the hero + filters + sessions don't fit in one viewport.
.program-page {
  min-height: 100vh;

  @media (min-width: 1024px) {
    height: 100vh;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  ::v-deep #main-content {
    @media (min-width: 1024px) {
      flex: 1 1 0;
      min-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
  }

  // The router slot inside #main-content (the data-provider stack
  // ending in the layout `<div>` we render) — make every wrapper
  // pass the height down so the eventual scroll lives on the
  // sessions list, not on the page.
  ::v-deep #main-content > * {
    @media (min-width: 1024px) {
      flex: 1 1 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }
}

*:focus-visible {
  outline: 2px solid var(--color-primary, #047873);
  outline-offset: 2px;
}

// ── Hero overlay pills ────────────────────────────────────────────────────
// Greeting pill (avatar + name) sits in the top-LEFT of the hero,
// "My Programs" pill (with optional saved-count badge) sits top-RIGHT,
// directly over the admin's video/image — no separate header strip.
.prog-hero__greeting {
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 16px 4px 12px;
  border-radius: 9999px;
  background: #ffffff;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1);
  font-family: 'Poppins', sans-serif;
  max-width: calc(100% - 48px);

  @media (max-width: 640px) {
    top: 14px;
    left: 14px;
    padding: 3px 12px 3px 10px;
    gap: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.prog-hero__avatar {
  width: 24px;
  flex-shrink: 0;
  border-radius: 50%;

  ::v-deep .avatar__image {
    object-fit: cover;
  }

  @media (max-width: 640px) {
    width: 20px;
  }
}

.prog-hero__hi {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;

  @media (max-width: 640px) {
    display: none;
  }
}

.prog-hero__name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 18ch;

  @media (max-width: 640px) {
    font-size: 12px;
    max-width: 11ch;
  }
}

.prog-hero__wave {
  width: 15px;
  height: 15px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 13px;
    height: 13px;
  }
}

.prog-hero__cta {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 9999px;
  border: none;
  background: #ffffff;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1);
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s, box-shadow 0.15s;

  > i {
    color: var(--color-primary, #047873);
    font-size: 12px;
  }

  &:hover,
  &:focus-visible {
    background: #f9fafb;
    transform: translateY(-1px);
    box-shadow: 0 5px 16px rgba(0, 0, 0, 0.13);
    outline: none;
  }

  @media (max-width: 640px) {
    top: 14px;
    right: 14px;
    padding: 5px 10px;
    font-size: 12px;
    gap: 5px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    > i {
      font-size: 11px;
    }
  }
}

// ── Mobile-only pills row ──────────────────────────────────────────
// On mobile the greeting + My Programs pills are lifted out of the hero
// banner and rendered above the promoted message (LiveStream's
// `mobile-top` slot). The in-banner pills are hidden there instead.
@media (max-width: 1100px) {
  .prog-hero__greeting,
  .prog-hero__cta {
    display: none;
  }
}

.prog-mobile-pills {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  &__greeting {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 4px 14px 4px 10px;
    border-radius: 9999px;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-family: 'Poppins', sans-serif;
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    padding: 6px 14px;
    border-radius: 9999px;
    border: none;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    color: #111827;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    > i {
      color: var(--color-primary, #047873);
      font-size: 12px;
    }

    &:hover,
    &:focus-visible {
      background: #f9fafb;
      outline: none;
    }
  }
}

// Dark mode — the mobile pills sit on the dark page background (not over
// the hero image), so the white pills + dark text need theming.
body[data-theme='dark'] {
  .prog-mobile-pills__greeting {
    background: var(--t-surface-elevated);
    box-shadow: var(--t-card-shadow-lg);

    .prog-hero__hi {
      color: var(--t-text-secondary);
    }
    .prog-hero__name {
      color: var(--t-text);
    }
  }

  .prog-mobile-pills__cta {
    background: var(--t-surface-elevated);
    color: var(--t-text);
    box-shadow: var(--t-card-shadow-lg);

    > i {
      color: var(--t-primary);
    }

    &:hover,
    &:focus-visible {
      background: var(--t-surface-alt);
    }
  }
}

// Pure media banner — the admin's "Live video/image URL" renders here at
// its natural aspect ratio (object-fit: contain) so the full asset is
// always visible, never cropped. The container has a min-height so it
// reads as a proper banner even when the asset is short, and a max-height
// to prevent very tall portrait images from dominating the page. Empty
// space around the image is filled with a soft brand tint so letterboxing
// reads as intentional rather than broken.
.prog-hero {
  position: relative;
  background: linear-gradient(135deg, #f0fbf9 0%, #d8efed 100%);
  width: 100%;
  // Mobile baseline — desktop bumps up at the breakpoints below so
  // the admin's banner reads with proper presence on bigger screens.
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  // Rounded corners — match the side card's 14px radius so the
  // hero reads as a card alongside it rather than a flush banner.
  border-radius: 14px;
  flex-shrink: 0; // never let the flex column shrink the hero

  @media (max-width: 640px) {
    border-radius: 12px;
  }

  @media (min-width: 768px) {
    height: 260px;
  }
  @media (min-width: 1280px) {
    height: 300px;
  }
  @media (min-width: 1600px) {
    height: 340px;
  }

  // Empty state (no admin URL set yet) — slightly darker brand
  // gradient so the section still reads as intentional.
  &--empty {
    background: linear-gradient(
      135deg,
      #0a3d35 0%,
      var(--color-primary, #047873) 100%
    );
  }

  &__media {
    display: block;
    width: 100%;
    height: 100%;
    // `cover` fills the entire banner — no empty gradient gutters on
    // either side when the asset's aspect ratio doesn't perfectly
    // match the container. Edges may be trimmed for very tall /
    // square assets, but landscape banners (the common case) read
    // edge-to-edge.
    object-fit: cover;
    object-position: center;
    pointer-events: none;

    // Iframes (YouTube/Vimeo/etc.) ignore object-fit but should still
    // fill the banner. Cover the full area; black-bar letterboxing is
    // controlled by the embed itself.
    &--iframe {
      object-fit: initial;
      border: 0;
      pointer-events: auto;
    }
  }
}

// ── Filters (inline label + pill dropdown) ───────────────────────────────
// ── Mobile agenda (in-page) ──────────────────────────────────────────
// On mobile the agenda — the hero picture, filters, day nav and session
// cards — is collapsed behind an "Agendas" button. Tapping it reveals
// the agenda in place on THIS page (an inline expander, not a separate
// full-screen page); the ✕ in its header collapses it again. On desktop
// the button + header are hidden and the agenda renders inline exactly
// as before — the wrappers use `display: contents` so they add no box.
.prog-agenda-btn {
  display: none;
}
.prog-agenda-panel {
  display: contents;
}
.prog-agenda-panel__body {
  display: contents;
}
.prog-agenda-panel__head {
  display: none;
}

@media (max-width: 640px) {
  // Trigger — a clean white card. The large bottom margin keeps it clear
  // of the floating bottom-nav pill when it's the last thing on the
  // (collapsed) page.
  .prog-agenda-btn {
    display: flex;
    align-items: center;
    gap: 14px;
    width: calc(100% - 32px);
    margin: 16px 16px 104px;
    padding: 14px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
    cursor: pointer;
    text-align: left;
    font-family: 'Poppins', sans-serif;
    -webkit-tap-highlight-color: transparent;
    transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;

    &:active {
      transform: translateY(1px);
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 46px;
      height: 46px;
      flex-shrink: 0;
      border-radius: 12px;
      background: var(--color-primary, #047873);
      color: #ffffff;
      font-size: 18px;
    }

    &__text {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    &__title {
      font-size: 15px;
      font-weight: 700;
      color: #111827;
    }

    &__sub {
      font-size: 12.5px;
      font-weight: 500;
      color: #6b7280;
      margin-top: 2px;
    }

    &__chev {
      margin-left: auto;
      color: #9ca3af;
      font-size: 14px;
    }
  }

  // Collapsed until the bottom-nav Agenda opens it as a full-screen page
  // (same pattern as the chat panel): fixed, clear of the bottom-nav pill,
  // with its own scrolling body.
  .prog-agenda-panel {
    display: none;
  }
  .prog-agenda-panel--open {
    display: block;
  }
  .prog-agenda-panel--overlay {
    display: flex;
    flex-direction: column;
    position: fixed;
    // Sits between the page and the Navbar (z-index 30, which also holds
    // the bottom-nav pill) so the navbar and bottom menu stay visible.
    top: var(--agenda-top, 64px);
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--background-color, #f3f4f6);
    z-index: 25;
    overflow: hidden;
  }
  .prog-agenda-panel__body {
    display: block;
  }
  .prog-agenda-panel--overlay .prog-agenda-panel__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    // Clear the floating bottom-menu pill so the last session isn't hidden.
    padding-bottom: 104px;
  }
  // Keep the banner aligned with the filters and cards instead of
  // running edge to edge.
  .prog-agenda-panel--overlay .prog-hero {
    width: calc(100% - 24px);
    margin: 12px 12px 0;
  }

  // In-page header row carrying the close (✕) button.
  .prog-agenda-panel__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;
  }

  .prog-agenda-panel__title {
    margin: 0;
    font-family: 'Poppins', sans-serif;
    font-size: 17px;
    font-weight: 700;
    color: #111827;
  }

  .prog-agenda-panel__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: none;
    border-radius: 50%;
    background: #f3f4f6;
    color: #111827;
    font-size: 16px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background: #e5e7eb;
    }
  }
}

// Dark mode for the trigger card + in-page agenda header.
body[data-theme='dark'] {
  .prog-agenda-btn {
    background: var(--t-surface-elevated, #131a24);
    border-color: var(--t-border, #1f2733);
  }
  .prog-agenda-btn__title {
    color: var(--t-text, #e5e7eb);
  }
  .prog-agenda-btn__sub {
    color: var(--t-text-secondary, #9ca3af);
  }
  .prog-agenda-panel__head {
    background: var(--t-surface-elevated, #131a24);
    border-bottom-color: var(--t-border, #1f2733);
  }
  .prog-agenda-panel__title {
    color: var(--t-text, #e5e7eb);
  }
  .prog-agenda-panel__close {
    background: var(--t-surface-alt, #1a222d);
    color: var(--t-text, #e5e7eb);
  }
}

// Compact pill-style dropdowns with their label sitting inline to the
// left ("Filter by room:  [ All Rooms ⌄ ]"). Flows as one row on
// wide viewports, wraps to multi-line on narrow ones.
.prog-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 10px;
  column-gap: 18px;
  padding: 14px 24px;
  // Transparent strip — only the bottom separator remains so the
  // row reads as a divider over the page background instead of a
  // white card. Dark-mode border softness comes via _variables.scss.
  background: transparent;
  border-bottom: 1px solid var(--t-border-soft, #e5e7eb);
  flex-shrink: 0;

  @media (max-width: 640px) {
    // Comfortable outer padding; Room + Category still fit on one
    // row and Date wraps below them.
    padding: 20px 16px;
    column-gap: 16px;
    row-gap: 20px;
  }
}

.prog-filter {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: 'Poppins', sans-serif;

  // Mobile: natural content-width pills, tighter gap so Room and
  // Category both fit on the first row. Date wraps below on its own.
  @media (max-width: 640px) {
    gap: 6px;
  }

  // Date dropdown belongs only on mobile — desktop uses the day-tabs.
  // On mobile it wraps to its own row beneath Room + Category.
  &--date {
    @media (min-width: 641px) {
      display: none;
    }
    @media (max-width: 640px) {
      flex-basis: 100%;

      // Let the date pill size naturally so the full day label (e.g.
      // "Tuesday, APR 21") reads without truncating to "Tuesday, Apr…".
      .prog-filter__trigger {
        min-width: 152px;
      }
      .prog-filter__value {
        max-width: none;
      }
    }
  }

  // Previous / next-day arrows flanking the mobile date pill. They step
  // `selectedDate` one day at a time and disable at the first / last day.
  .prog-date-nav {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    padding: 0;
    background: #ffffff;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    color: var(--color-primary, #047873);
    font-size: 13px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s, opacity 0.15s;

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      border-color: var(--color-primary, #047873);
      background: #f0faf9;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      color: #9ca3af;
    }
  }

  // Search input lives at the start of the filters row. Desktop:
  // stays on the left; the first filter pill (Room) uses
  // margin-left: auto to push the rest of the filters to the right.
  // Mobile: full-width first row, filters wrap below.
  &--search {
    position: relative;
    flex: 0 1 320px;
    min-width: 200px;

    @media (max-width: 640px) {
      flex-basis: 100%;
      min-width: 0;
    }
  }

  // First pill (Room) sits immediately after the search on desktop —
  // margin-left: auto pushes it (and everything after it) to the far
  // right of the row. On mobile the rule is disabled so Room stays
  // next to Category on the second row beneath the search.
  &--first-pill {
    @media (min-width: 641px) {
      margin-left: auto;
    }
  }

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    white-space: nowrap;

    @media (max-width: 640px) {
      font-size: 14px;
    }
  }

  // Compact pill-shaped trigger: thin border, rounded full, tight
  // horizontal padding, text + chevron sitting naturally side-by-side.
  &__trigger {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
    font-family: 'Poppins', sans-serif;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;

    @media (max-width: 640px) {
      padding: 5px 10px;
      gap: 6px;
    }

    &::-moz-focus-inner {
      border: 0;
      padding: 0;
    }

    &:hover {
      border-color: #d1d5db;
    }

    &:focus-visible {
      border-color: #d1d5db;
    }

    &--open {
      border-color: #d1d5db;
    }
  }

  // The selected value text inside the pill — medium weight.
  &__value {
    font-size: 13px;
    font-weight: 500;
    color: #3d4151;
    line-height: 1.3;
    white-space: nowrap;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 640px) {
      font-size: 13px;
      max-width: 110px;
    }
  }

  &__chevron {
    color: #6b7280;
    font-size: 12px;
    flex-shrink: 0;
    transition: transform 0.2s, color 0.15s;

    &--open {
      color: var(--color-primary, #047873);
      transform: rotate(180deg);
    }

    @media (max-width: 640px) {
      font-size: 10px;
    }
  }

  // Popover panel — anchors to the LEFT of the filter by default so
  // it drops below the trigger without spilling off the left edge of
  // the viewport (important for the Room filter which sits at the
  // far left of the row). Rightmost filters (Category) override to
  // right-anchor so they don't overflow to the right.
  &__panel {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: auto;
    z-index: 50;
    min-width: 200px;
    max-width: min(320px, 92vw);
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 10px 20px -4px rgb(0 0 0 / 0.1),
      0 4px 8px -4px rgb(0 0 0 / 0.08);
    padding: 6px;
    max-height: 280px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;

    @media (max-width: 640px) {
      min-width: 180px;
    }
  }

  // Category sits at the right side of the row — anchor its panel
  // to the right so it doesn't overflow off the right edge.
  &--category &__panel {
    left: auto;
    right: 0;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    background: transparent;
    border-radius: 10px;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    text-align: left;
    transition: background 0.12s, color 0.12s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;

    &:hover {
      background: #f3f4f6;
    }

    &--active {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
      font-weight: 700;

      &:hover {
        background: #e6f5f4;
      }
    }

    @media (max-width: 640px) {
      padding: 10px 10px;
      font-size: 13px;
    }
  }

  &__option-check {
    color: var(--color-primary, #047873);
    font-size: 12px;
    flex-shrink: 0;
  }

  // Search input — matches the filter pill: white background, same
  // 1px border, rounded full, same font size and padding as a trigger.
  &__search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 12px;
    pointer-events: none;
  }

  &__search {
    width: 100%;
    padding: 7px 14px 7px 34px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    font-size: 13px;
    font-weight: 500;
    color: #111827;
    font-family: 'Poppins', sans-serif;
    outline: none;
    line-height: 1.3;
    transition: border-color 0.15s, box-shadow 0.15s;

    &::placeholder {
      color: #9ca3af;
      font-weight: 400;
    }

    &:hover {
      border-color: #d1d5db;
    }

    &:focus {
      border-color: #d1d5db;
    }

    @media (max-width: 640px) {
      padding: 9px 14px 9px 34px;
      font-size: 14px;
    }
  }

  // Make .prog-filter itself a positioning context so the absolute
  // popover anchors to the trigger, not to the whole filters row.
  position: relative;

  // Clear button — compact, only shown when a filter is active.
  &__clear {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 40px;
    padding: 0 12px;
    align-self: flex-end;
    border-radius: 10px;
    border: 1.5px solid transparent;
    background: rgba(4, 120, 115, 0.08);
    color: var(--color-primary, #047873);
    font-size: 12px;
    font-weight: 700;
    font-family: 'Poppins', sans-serif;
    cursor: pointer;
    flex-shrink: 0;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;
    transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;

    &:hover {
      background: rgba(4, 120, 115, 0.14);
      border-color: rgba(4, 120, 115, 0.25);
    }

    &:focus-visible {
      outline: none;
      border-color: var(--color-primary, #047873);
      box-shadow: 0 0 0 3px rgba(4, 120, 115, 0.2);
    }

    @media (max-width: 640px) {
      height: 36px;
      padding: 0 10px;
      border-radius: 8px;

      // Hide the "Clear" text label on phones; icon alone is enough
      // and keeps the row narrow enough for all three dropdowns.
      font-size: 0;
      gap: 0;

      i {
        font-size: 13px;
      }
    }
  }
}

.prog-filter__download {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 40px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1.5px solid transparent;
  background: rgba(4, 120, 115, 0.08);
  color: var(--color-primary, #047873);
  font-size: 12px;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  flex-shrink: 0;
  outline: none;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: rgba(4, 120, 115, 0.14);
    border-color: rgba(4, 120, 115, 0.25);
  }
}
.prog-download-error {
  margin: 8px 24px 0;
  font-size: 13px;
  color: #b91c1c;
}

// Hide the legacy mobile date picker — the Date filter in
// .prog-filters replaces it so everything sits on one row.
.prog-date-picker {
  display: none !important;
}

// ── Day tabs ──────────────────────────────────────────────────────────────
.prog-days {
  display: flex;
  gap: 16px;
  padding: 20px 24px 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  flex-shrink: 0;

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  // Desktop only — hidden on mobile
  &--desktop {
    @media (max-width: 640px) {
      display: none;
    }
  }
}

// Mobile themed date picker — hidden on desktop
.prog-date-picker {
  display: none;
  position: relative;
  padding: 16px 16px 0;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 640px) {
    display: block;
  }

  // ── Trigger button ────────────────────────────────────────────────
  &__trigger {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 14px 16px;
    background: #ffffff;
    border: 1.5px solid #e5e7eb;
    border-radius: 14px;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
    transition: border-color 0.15s, box-shadow 0.15s;

    &:hover,
    &:focus {
      border-color: var(--color-primary, #047873);
      box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.08);
    }
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: #e6f5f4;
    color: var(--color-primary, #047873);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
  }

  &__selected {
    flex: 1;
    min-width: 0;
  }

  &__day {
    display: block;
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
  }

  &__count {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    margin-top: 2px;
  }

  &__chevron {
    font-size: 12px;
    color: #6b7280;
    transition: transform 0.2s;
    flex-shrink: 0;

    &--open {
      transform: rotate(180deg);
      color: var(--color-primary, #047873);
    }
  }

  // ── Dropdown panel ────────────────────────────────────────────────
  &__panel {
    position: absolute;
    top: calc(100% + 6px);
    left: 16px;
    right: 16px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1),
      0 4px 6px -4px rgb(0 0 0 / 0.1);
    z-index: 50;
    overflow: hidden;
  }

  &__panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #f9fafb;
    border-bottom: 1px solid #f3f4f6;
  }

  &__panel-title {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__panel-close {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: #e5e7eb;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    transition: background 0.15s;

    &:hover {
      background: #d1d5db;
    }
  }

  &__options {
    padding: 8px;
    max-height: 320px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  // ── Individual date option ────────────────────────────────────────
  &__option {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 14px;
    border: 1.5px solid transparent;
    border-radius: 12px;
    background: #ffffff;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    transition: background 0.12s, border-color 0.12s;

    &:hover {
      background: #f9fafb;
      border-color: #e5e7eb;
    }

    &--active {
      background: #e6f5f4;
      border-color: var(--color-primary, #047873);

      .prog-date-picker__option-num {
        background: var(--color-primary, #047873);
        color: #ffffff;
      }

      .prog-date-picker__option-name {
        color: var(--color-primary, #047873);
      }

      &:hover {
        background: #e6f5f4;
      }
    }
  }

  &__option-num {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #f3f4f6;
    color: #111827;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
    flex-shrink: 0;
    transition: background 0.12s, color 0.12s;
  }

  &__option-info {
    flex: 1;
    min-width: 0;
  }

  &__option-name {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
    transition: color 0.12s;
  }

  &__option-count {
    display: block;
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }

  &__option-check {
    font-size: 14px;
    color: var(--color-primary, #047873);
    flex-shrink: 0;
  }
}

// Reuse the notification fade transition for the panel
.notif-fade-enter-active,
.notif-fade-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.notif-fade-enter,
.notif-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.prog-day {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  background: #ffffff;
  transition: border-color 0.15s;
  flex-shrink: 0;
  min-width: 160px;

  @media (max-width: 640px) {
    padding: 10px 14px;
    min-width: 130px;
    gap: 8px;
  }

  &:hover {
    border-color: var(--color-primary, #047873);
  }

  &--active {
    border-color: var(--color-primary, #047873);
    border-width: 2px;
  }

  &__num {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    line-height: 1;

    @media (max-width: 640px) {
      font-size: 22px;
    }
  }

  &--active &__num {
    color: var(--color-primary, #047873);
  }

  &__right {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__month {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    line-height: 1.2;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    line-height: 1.2;
  }

  &__count {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.2;
  }
}

// ── Session cards ─────────────────────────────────────────────────────────
.prog-sessions {
  padding-top: 20px;

  // Mobile: the promoted card now sits ABOVE the agenda, so the
  // sessions list is the last content on the page. Keep a generous
  // bottom space so the final card can scroll clear of the fixed
  // bottom-nav instead of being hidden behind it.
  @media (max-width: 1100px) {
    padding-bottom: 96px !important;
    // Trim the side padding (Tailwind `px-4` = 16px) on mobile so the
    // session cards get more width and their content (e.g. the
    // "x of y seats left" pill) isn't squeezed / cut off.
    padding-left: 8px;
    padding-right: 8px;
  }

  // Desktop: the page is height-bounded, and this is the ONLY
  // child of the inner column that should grow + scroll. Everything
  // else (hero, filters, day tabs) sits at its natural height
  // above; the session list takes over the remaining space.
  @media (min-width: 1024px) {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
  }
}

.prog-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;

  // Text variant — shown when the selected day has no sessions.
  &--text {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    font-family: 'Poppins', sans-serif;
    text-align: center;
  }
}

// Shown when the admin has the agenda turned off for this platform.
.prog-unavailable {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 72px 24px;
  color: #6b7280;

  > i {
    font-size: 40px;
    color: #d1d5db;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: #374151;
    margin: 0 0 6px;
  }

  &__text {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    max-width: 360px;
  }
}

body[data-theme='dark'] {
  .prog-unavailable {
    color: var(--t-text-secondary);

    > i {
      color: var(--t-text-disabled);
    }
    .prog-unavailable__title {
      color: var(--t-text);
    }
    .prog-unavailable__text {
      color: var(--t-text-secondary);
    }
  }
}

.session-grid {
  display: grid;
  // Single column on all sizes so each session card spans the full
  // width of the content area (wider cards on web instead of the
  // previous 2-up layout).
  grid-template-columns: 1fr;
  gap: 14px;

  // Mobile: App.vue's global bottom-nav clearance rule adds
  // `padding-bottom: 80px !important` to `.session-grid`, which opens a
  // big gap before the promoted card below. The promoted card carries
  // its own clearance, so cancel it here. `!important` beats the global.
  @media (max-width: 1100px) {
    padding-bottom: 0 !important;
  }
}

.session-card {
  display: flex;
  align-items: stretch;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
  min-width: 0;

  &--ended {
    background: #f9fafb;
    opacity: 0.78;

    .session-card__title,
    .session-card__start {
      color: #6b7280;
    }

    .session-card__end,
    .session-card__speaker,
    .session-card__meta-item {
      color: #6b7280;
    }

    .session-card__clock {
      color: #6b7280;
    }

    .session-card__status {
      background: #f3f4f6 !important;
      color: #6b7280 !important;
      border-color: #e5e7eb !important;
    }
  }

  &__ended-label {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
  }

  &__time-col {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 20px 16px;
    // Fixed width (not min-width) so the divider between the time column
    // and the content sits at the SAME x-position on every card — a wider
    // status pill ("Attending" / "Available" / "Locked") can no longer
    // grow the column and knock the separators out of alignment. Sized to
    // comfortably hold the widest status pill so nothing clips.
    flex: 0 0 116px;
    box-sizing: border-box;
    border-right: 1px solid #f3f4f6;
    gap: 4px;

    @media (max-width: 480px) {
      padding: 16px 12px;
      flex-basis: 104px;
    }
  }

  &__clock {
    font-size: 13px;
    color: var(--color-primary, #047873);
    margin-bottom: 4px;
  }

  &__start {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    line-height: 1;
  }

  &__end {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.2;
    margin-bottom: 8px;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;

    &--available {
      border: 1.5px solid var(--color-primary, #047873);
      color: var(--color-primary, #047873);
      background: transparent;
    }

    &--attending {
      background: var(--color-primary, #047873);
      color: #ffffff;
    }

    &--locked {
      border: 1.5px solid #6b7280;
      color: #6b7280;
      background: transparent;
    }

    &--ended {
      border: 1.5px solid #d1d5db;
      color: #6b7280;
      background: transparent;
    }
  }

  &__body {
    flex: 1;
    // Right padding reserves room for the 36 px circular play button.
    padding: 18px 64px 18px 22px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    // Uniform 12 px row spacing between title, speakers, meta, actions.
    gap: 12px;

    @media (max-width: 480px) {
      padding: 14px 54px 14px 14px;
    }
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    line-height: 1.3;
  }

  &__speaker {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 10px;
  }

  // ── Title row (title + inline category chips) ───────────────
  &__title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin: 0;
  }

  // Matches the Lobby agenda card.
  &__subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    overflow-wrap: anywhere;
  }

  // Category chips — sit inline with the title
  &__category-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    background: #e6f5f4;
    color: var(--color-primary, #047873);
    border: 1px solid transparent;
    border-radius: 9999px;
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1;
    flex-shrink: 0;

    i {
      font-size: 8px;
      opacity: 0.85;
    }
  }

  // ── Clickable speaker chips (with avatar icon) ──────────────
  // ── Speakers pill — leading mic icon + comma-separated names.
  //    Single container so it reads as one grouped "Speakers:" chip
  //    rather than a participant-style list.
  &__speakers {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 14px 4px 4px;
    background: #f3f4f6;
    border: 1.5px solid #e5e7eb;
    border-radius: 9999px;
    max-width: 100%;
    // Align the pill to its content width so the flex container's
    // gap: 12px is the only vertical spacer between rows.
    align-self: flex-start;
  }

  // Primary-teal circular microphone badge at the start of the pill
  &__speaker-mic-lead {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
  }

  &__speakers-names {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #111827;
    line-height: 1.2;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__speaker-name-btn {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    transition: color 0.12s;

    &:hover {
      color: var(--color-primary, #047873);
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }

  &__speakers-sep {
    color: #6b7280;
    margin: 0 2px 0 0;
  }

  // ── Seat availability ───────────────────────────────────────
  // Pill chip variant — sits beside the track pill so the two read
  // as a matched pair. Background tint is driven by the seat-class
  // modifier so the user can spot scarcity at a glance.
  &__seats-pill {
    padding: 3px 10px;
    border-radius: 9999px;
    font-weight: 600;
    font-size: 12.5px;
    line-height: 1.4;
    border: 1px solid transparent;
    gap: 6px;

    i {
      font-size: 11px;
      color: inherit;
    }

    &.session-card__seats--ok {
      background: rgba(21, 128, 61, 0.12);
      color: #15803d;
      border-color: rgba(21, 128, 61, 0.25);
    }
    &.session-card__seats--low {
      background: rgba(180, 83, 9, 0.12);
      color: #b45309;
      border-color: rgba(180, 83, 9, 0.3);
    }
    &.session-card__seats--full {
      background: rgba(220, 38, 38, 0.12);
      color: #dc2626;
      border-color: rgba(220, 38, 38, 0.3);
    }
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    column-gap: 16px;
    row-gap: 6px;
    align-items: center;
  }

  // Row that holds Track + Seats + Participants chips. Wraps on
  // narrow cards so things stack gracefully when there isn't enough
  // horizontal room — but Track + Seats are grouped together via
  // `.session-card__chip-pair` so they always stay on the same line.
  &__meta-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 10px;
    row-gap: 6px;
    min-width: 0;

    @media (max-width: 480px) {
      column-gap: 8px;
    }

    .session-card__meta-item {
      white-space: nowrap;
      flex-shrink: 0;
    }

    // Mobile: long track names (e.g. parallel sessions) wrap inside the
    // pill instead of being clipped by the card's `overflow: hidden`.
    @media (max-width: 640px) {
      .session-card__track-pill {
        white-space: normal;
        flex-shrink: 1;
        min-width: 0;
        max-width: 100%;
        overflow-wrap: anywhere;
        align-items: flex-start;
        border-radius: 14px;

        i {
          flex-shrink: 0;
          margin-top: 3px;
        }
      }
    }
  }

  // Track + Seats pills. They sit together on one line when there's
  // room, but are allowed to wrap (and shrink) so a long "x of y seats
  // left" pill drops to the next line on narrow cards instead of
  // overflowing and getting clipped by the card's `overflow: hidden`.
  &__chip-pair {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    min-width: 0;

    @media (max-width: 640px) {
      max-width: 100%;
    }
  }

  &__meta-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: #6b7280;

    i {
      font-size: 12px;
    }
  }

  // Track pill uses the admin's configured track color as its
  // background. Text/icon color is picked on the fly (see
  // pickContrastingTextColor) so dark tracks get white text and
  // light tracks get near-black text for readability.
  &__track-pill {
    padding: 3px 10px;
    border-radius: 9999px;
    font-weight: 600;
    font-size: 12.5px;
    line-height: 1.4;
    border: 1px solid transparent;
    gap: 6px;

    i {
      font-size: 11px;
      color: inherit;
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: flex-start;
    // `margin-top: auto` pushes View Details to the bottom when
    // the card has extra vertical space; `gap: 12px` on the body
    // still guarantees a 12 px minimum above the actions row.
    margin-top: auto;
  }

  // Circular green Join-Now button — anchored bottom-right
  &__play-btn {
    position: absolute;
    right: 16px;
    bottom: 16px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid rgba(22, 163, 74, 0.35);
    background: rgba(22, 163, 74, 0.1);
    color: #16a34a;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    box-shadow: none;
    transition: background 0.15s, transform 0.15s;
    z-index: 2;

    i {
      margin-left: 2px;
    } // optical center of the triangle

    &:hover {
      background: rgba(22, 163, 74, 0.2);
      transform: scale(1.05);
    }

    &:focus-visible {
      outline: 3px solid rgba(22, 163, 74, 0.4);
      outline-offset: 2px;
    }

    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
      right: 12px;
      bottom: 12px;
      font-size: 11px;
    }
  }

  // Small green dot marking a session that is live right now.
  &__live-dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    margin-right: 6px;
    border-radius: 50%;
    background: #16a34a;
    box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.2);
    vertical-align: middle;
    animation: pulse-live 1.6s ease-in-out infinite;
  }

  &__details-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    background: #ffffff;
    color: var(--color-primary, #047873);
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
    font-family: 'Poppins', sans-serif;

    i {
      font-size: 10px;
    }

    &:hover {
      background: #f9fafb;
      border-color: var(--color-primary, #047873);
    }
  }

  &__join-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 14px;
    background: #16a34a;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    transition: opacity 0.15s;

    &:hover {
      opacity: 0.9;
    }
  }

  &__join-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
    animation: pulse-live 1.6s ease-in-out infinite;
  }

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    flex-shrink: 0;
    align-self: flex-start;
    margin: 16px 16px 0 0;
    font-size: 14px;
    transition: background 0.15s, color 0.15s;

    @media (max-width: 640px) {
      width: 26px;
      height: 26px;
      border-radius: 6px;
      margin: 12px 12px 0 0;
      font-size: 11px;
    }

    &:hover {
      background: #e5e7eb;
    }

    &--booked {
      background: var(--color-primary, #047873);
      color: #ffffff;

      &:hover {
        opacity: 0.85;
        background: var(--color-primary, #047873);
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

// ── Session modal ─────────────────────────────────────────────────────────
.session-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.session-modal {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 28px;

  &__close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;

    &:hover {
      background: #e5e7eb;
    }
  }

  &__header {
    margin-bottom: 20px;
    padding-right: 32px;
  }

  &__time {
    font-size: 14px;
    color: var(--color-primary, #047873);
    font-weight: 600;
    display: block;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 6px;
  }

  &__subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 10px;
  }

  &__speaker {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0 6px;
    font-size: 14px;
    color: #374151;
    margin-bottom: 10px;

    i {
      color: #6b7280;
      margin-right: 2px;
    }
  }

  // Clickable speaker name — opens that speaker's detail popup.
  &__speaker-btn {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: 600;
    color: var(--color-primary, #047873);
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover,
    &:focus-visible {
      text-decoration-thickness: 2px;
      outline: none;
    }
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 14px;
    color: #6b7280;

    span {
      display: inline-flex;
      align-items: center;
      gap: 5px;

      i {
        font-size: 12px;
      }
    }
  }

  &__description {
    font-size: 14px;
    color: #374151;
    line-height: 1.7;
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
    margin-bottom: 20px;
  }

  &__footer {
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
  }

  &__join-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 22px;
    background: #16a34a;
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    transition: opacity 0.15s;

    &:hover {
      opacity: 0.9;
    }
  }

  &__book-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    font-family: 'Poppins', sans-serif;

    &:hover {
      opacity: 0.85;
    }

    &--booked {
      background: #f3f4f6;
      color: #374151;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

::v-deep .html-content {
  p {
    margin-bottom: 12px;
  }
  h1,
  h2,
  h3 {
    font-weight: 700;
    margin-bottom: 8px;
  }
  ul,
  ol {
    padding-left: 20px;
    margin-bottom: 12px;
  }
}

@keyframes pulse-live {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.55;
    transform: scale(0.85);
  }
}
</style>
